import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { usePrices } from '../context/PriceContext';
import PayPalButton from '../components/payment/PayPalButton';
import { analytics } from '../services/analytics';

const PaymentContainer = styled.div`
  padding: ${props => props.theme.spacing.large};
  max-width: 800px;
  margin: 0 auto;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: ${props => props.theme.spacing.large};
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
  text-align: center;
`;

const PaymentCard = styled.div`
  background-color: ${props => props.theme.colors.background.tertiary};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${props => props.theme.spacing.xlarge};
  box-shadow: ${props => props.theme.shadows.large};
  border: 1px solid ${props => props.theme.colors.border.primary};
`;

const OrderSummary = styled.div`
  margin-bottom: ${props => props.theme.spacing.xlarge};
  padding: ${props => props.theme.spacing.large};
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.medium};
`;

const SummaryTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: ${props => props.theme.spacing.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.small};
  font-size: 1.1rem;
  color: ${props => props.theme.colors.text.primary};
`;

const TotalRow = styled(SummaryRow)`
  font-weight: 700;
  font-size: 1.3rem;
  margin-top: ${props => props.theme.spacing.medium};
  border-top: 2px solid ${props => props.theme.colors.border.primary};
  padding-top: ${props => props.theme.spacing.medium};
`;

const BackButton = styled.button`
  background-color: ${props => props.theme.colors.background.secondary};
  color: ${props => props.theme.colors.text.primary};
  border: 1px solid ${props => props.theme.colors.border.primary};
  padding: ${props => props.theme.spacing.medium};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: ${props => props.theme.spacing.large};
  transition: background-color ${props => props.theme.transitions.medium};
  
  &:hover {
    background-color: ${props => props.theme.colors.background.tertiary};
  }
`;

const ErrorMessage = styled.div`
  color: #f44336;
  text-align: center;
  font-size: 1.2rem;
  margin: ${props => props.theme.spacing.large} 0;
`;

const PaymentPage: React.FC = () => {
  const { cartItems, clearCart } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);

  // Get payment details from URL parameters
  const finalAmount = parseFloat(searchParams.get('amount') || '0');
  const originalAmount = parseFloat(searchParams.get('originalAmount') || '0');
  const promoCode = searchParams.get('promoCode');
  const promoDiscount = parseFloat(searchParams.get('promoDiscount') || '0');

  // Verificar si hay items en el carrito y datos válidos
  useEffect(() => {
    if (cartItems.length === 0 || finalAmount === 0) {
      navigate('/cart');
    }
  }, [cartItems, finalAmount, navigate]);

  const handlePayPalSuccess = async (details: any) => {
    setIsProcessing(true);
    console.log('PayPal payment successful:', details);
    
    // Apply discount if there's one
    if (promoCode) {
      try {
        await fetch('https://canvasoft.com:8487/canvamanager/descuentos/aplicar', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ codigo: promoCode }),
        });
        console.log('Discount applied:', promoCode);
      } catch (error) {
        console.error('Error applying discount:', error);
      }
    }
    
    // Track successful purchase with final amount
    analytics.purchase(details.id, finalAmount, cartItems);
    
    setTimeout(() => {
      clearCart();
      setIsProcessing(false);
      navigate('/payment-success');
    }, 1000);
  };

  const handlePayPalError = (error: any) => {
    console.error('PayPal payment error:', error);
    setIsProcessing(false);
    alert('Error en el pago. Por favor intenta de nuevo.');
  };

  const handlePayPalCancel = () => {
    console.log('PayPal payment cancelled');
    setIsProcessing(false);
  };

  const handleBack = () => {
    navigate('/cart');
  };

  if (cartItems.length === 0 || finalAmount === 0) {
    return (
      <PaymentContainer>
        <ErrorMessage>
          No hay items en el carrito para procesar el pago.
        </ErrorMessage>
      </PaymentContainer>
    );
  }

  return (
    <PaymentContainer>
      <BackButton onClick={handleBack}>
        ← Regresar al carrito
      </BackButton>
      
      <Title>Finalizar Compra</Title>
      
      <PaymentCard>
        <OrderSummary>
          <SummaryTitle>Resumen de tu pedido</SummaryTitle>
          <SummaryRow>
            <span>Imágenes seleccionadas:</span>
            <span>{cartItems.length}</span>
          </SummaryRow>
          {originalAmount > 0 && (
            <SummaryRow>
              <span>Subtotal:</span>
              <span>${originalAmount.toFixed(2)} USD</span>
            </SummaryRow>
          )}
          {promoCode && promoDiscount > 0 && (
            <SummaryRow>
              <span>Descuento ({promoCode} - {promoDiscount}%):</span>
              <span style={{ color: '#4caf50' }}>-${(originalAmount - finalAmount).toFixed(2)} USD</span>
            </SummaryRow>
          )}
          <TotalRow>
            <span>Total a pagar:</span>
            <span>${finalAmount.toFixed(2)} USD</span>
          </TotalRow>
        </OrderSummary>

        <PayPalButton
          amount={finalAmount}
          currency="USD"
          onSuccess={handlePayPalSuccess}
          onError={handlePayPalError}
          onCancel={handlePayPalCancel}
          disabled={isProcessing}
          cartItems={cartItems}
          promoCode={promoCode || undefined}
          promoDiscount={promoDiscount}
        />
      </PaymentCard>
    </PaymentContainer>
  );
};

export default PaymentPage;